import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Icon from '../icon/Icon';
import { gray40 } from '../vars';
import { hideNotificationPush } from '@shared/slices/notificationPushSlice';

import './NotificationPush.scss';

const AUTO_HIDE_MS = 5000;

export default function NotificationPush() {
  const dispatch = useDispatch();
  const { visible, notification } = useSelector((s) => s.notificationPush);
  const timeoutRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearTimer();
    timeoutRef.current = setTimeout(() => {
      dispatch(hideNotificationPush());
    }, AUTO_HIDE_MS);
  }, [clearTimer, dispatch]);

  useEffect(() => {
    if (!visible) {
      clearTimer();
      return undefined;
    }
    scheduleHide();
    return () => clearTimer();
  }, [visible, notification, clearTimer, scheduleHide]);

  const onMouseEnter = () => clearTimer();
  const onMouseLeave = () => {
    if (visible) scheduleHide();
  };

  const onClose = () => {
    clearTimer();
    dispatch(hideNotificationPush());
  };

  if (!visible) return null;

  const { header, content, iconContent } = notification;

  return (
    <div
      className="notification-push notification-push--open"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role="status"
      aria-live="polite"
    >
      <div className="notification-push__body">
        {header ? (
          <div className="notification-push__header">
            {iconContent ? (
              <div className="notification-push__iconWrapper">
                <Icon icon="Info" width={22} height={22} className="notification-push__icon" />
              </div>
            ) : null}
            <div className="notification-push__headerContent">{header}</div>
          </div>
        ) : null}
        {content != null && content !== '' ? (
          <div className="notification-push__content">{content}</div>
        ) : null}
      </div>
      <div className="notification-push__close">
        <button
          type="button"
          className="notification-push__closeButton"
          onClick={onClose}
          aria-label="Закрыть"
        >
          <Icon icon="Mini/Close" height={15} width={15} color={gray40} />
        </button>
      </div>
    </div>
  );
}
